import { BaseComponentImpl } from '../../../../types/implementations';
import { Building } from '../../../../types/protocols';
import { ComponentLifetime, TemplatesByType } from '../../TemplatesEditor/types';
import { resolveComponentSchedule, templateLookup } from '../../../../Helpers/maintenanceSchedule';
import { wellFormedComponents } from '../../../../Helpers/componentIntegrity';
import { MaintenanceDraftWithDone } from '../types';

/** A done component that contributes nothing to the preview inside the chosen window */
export interface PreviewGap {
    componentKey: string;
    componentName: string;
    /** The year of its first activity beyond the window, or null when it has none at all */
    firstActivityYear: number | null;
}

/** How far past the window to look for a component's first activity */
const LOOKAHEAD_YEARS = 200;

/**
 * Done components that show nothing in the preview because their first activity
 * lies beyond the window (a facade with a 40-year lifetime and a 10-year view)
 * or because they have none. Without this the preview looks like it lost them.
 */
export const findPreviewGaps = (options: {
    building: Building;
    draft: MaintenanceDraftWithDone;
    componentLifetimes: Record<string, ComponentLifetime>;
    maintenanceTemplates: TemplatesByType;
    yearsToShow: number;
    currentYear?: number;
}): PreviewGap[] => {
    const { building, draft, componentLifetimes, maintenanceTemplates, yearsToShow } = options;
    const currentYear = options.currentYear ?? new Date().getFullYear();
    const windowEnd = currentYear + yearsToShow;
    const findTemplate = templateLookup(maintenanceTemplates);
    const gaps: PreviewGap[] = [];

    wellFormedComponents(building.model?.structure?.components).forEach(componentData => {
        if (componentData.archivedAt || !componentData.specificType?.value) return;
        const entry = draft.components[componentData.key];
        if (!entry || entry.isDone !== true || entry.excluded === true) return;

        const component = new BaseComponentImpl(componentData);
        const componentType = component.specificType!.value!;
        const schedule = resolveComponentSchedule({
            component,
            entry,
            building,
            lifetime: componentLifetimes[componentType],
            typeTemplates: maintenanceTemplates[componentType] || [],
            findTemplate,
            window: { startYear: currentYear, endYear: currentYear + LOOKAHEAD_YEARS },
            isDraft: true
        });
        if (!schedule) return;

        const years = [
            ...(schedule.replacement?.years ?? []),
            ...schedule.activities.flatMap(activity => activity.dates.map(date => Number(date.slice(0, 4))))
        ];
        const firstActivityYear = years.length > 0 ? Math.min(...years) : null;
        if (firstActivityYear !== null && firstActivityYear < windowEnd) return;

        gaps.push({ componentKey: component.key, componentName: component.displayName(building), firstActivityYear });
    });

    return gaps.sort((a, b) => (a.firstActivityYear ?? Infinity) - (b.firstActivityYear ?? Infinity) || a.componentName.localeCompare(b.componentName, 'sv'));
};
